import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import {
  Check,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import type { TDashboardLayoutSource } from "@/types/(company)/dashboardLayout.interface";
import type { TLayoutSaveState } from "../types";

interface CommandBarProps {
  title: string;
  description: string;
  isEditing: boolean;
  saveState: TLayoutSaveState;
  widgetCount: number;
  availableCount: number;
  /** Whether this person has an arrangement of their own — drives whether Reset shows. */
  isCustomized: boolean;
  /** Which tier the current arrangement came from — drives the provenance chip. */
  source: TDashboardLayoutSource;
  /**
   * How old the figures on screen are, e.g. "Updated 4 mins ago" — or `null`
   * before the first refresh, when there is no honest age to report.
   */
  freshnessLabel: string | null;
  isRefreshing: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  onToggleEditing: () => void;
  onOpenLibrary: () => void;
  onReset: () => void;
  /** Re-attempts the write after a failed save. */
  onRetrySave: () => void;
}

const buildSourceLabel = (
  t: (key: string) => string,
): Record<TDashboardLayoutSource, string> => ({
  user: t("common:dashboard_grid.your_layout"),
  fallback: t("common:dashboard_grid.default_layout"),
});

/**
 * Provenance is carried by a dot, not by the chip's own ground.
 *
 * This used to be a filled chip — `success` for "yours". Green does not mean
 * "this arrangement is yours"; in this app it means an operation succeeded,
 * and a permanent green pill in the header of a page that also shows a green
 * "Saved" confirmation is two different meanings in one colour, a metre apart.
 *
 * One neutral chip with a coloured dot says the same thing at a tenth of the
 * ink: the dot distinguishes the two states, the word explains them, and
 * nothing borrows a semantic colour it does not mean.
 */
const SOURCE_DOT: Record<TDashboardLayoutSource, string> = {
  user: "bg-primary",
  fallback: "bg-border-strong",
};

/**
 * Header for the widgetable region.
 *
 * Customize is an explicit mode rather than always-on drag: a dashboard is read
 * far more often than it is arranged, and a canvas whose cards move when you
 * meant to click a link is worse than one extra button.
 *
 * The bar is laid out as three ranked blocks rather than one row of things,
 * which is the change most of the rest follows from:
 *
 * 1. *Identity*, two lines. The title with the description beneath it, instead
 *    of the description trailing along the same line behind two badges as the
 *    lowest-priority item and the first to truncate. Two lines is what gives
 *    the block a hierarchy at all — one line of four elements at four different
 *    sizes and colours has an order but no rank.
 *
 * 2. *Status*, read and never pressed: provenance, freshness, save state.
 *
 * 3. *Actions*, pressed and never merely read.
 *
 * Status and actions used to be interleaved — the widget count was a badge that
 * was secretly a button, and Refresh was a button whose label was a status
 * reading. Both of those are now on the correct side of the divider: the count
 * rides inside the Customize button it always triggered, and the freshness
 * reading is text with a refresh control next to it.
 *
 * Which mode you are in is a property of the whole bar, not of its right-hand
 * end. Swapping one button set for another is a change you have to have been
 * watching for; a tinted ground, a primary edge and a "Customizing" badge
 * against the title are not.
 */
export const CommandBar = ({
  title,
  description,
  isEditing,
  saveState,
  widgetCount,
  availableCount,
  isCustomized,
  source,
  freshnessLabel,
  isRefreshing,
  canRefresh,
  onRefresh,
  onToggleEditing,
  onOpenLibrary,
  onReset,
  onRetrySave,
}: CommandBarProps) => {
  const { t } = useTranslation(["common"]);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const sourceLabel = useMemo(() => buildSourceLabel(t), [t]);

  return (
    <>
      {/*
        Part of the canvas's own flow rather than pinned above it — it scrolls
        away with the rest of the page like any other header. While arranging
        a tall canvas, Escape or clicking off it are still how you leave the
        mode without scrolling back up to Done.
      */}
      <div
        className={cn(
          "widget-command-bar mb-2.5 flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-sm px-3.5 py-2",
          isEditing && "widget-command-bar-editing",
        )}
      >
        {/* ── 1. Identity ─────────────────────────────────────────────────
            `min-w-0` + `truncate` rather than `shrink-0`: this bar is a
            generic engine part reused by whichever page supplies `title`, and
            a longer one on a narrow screen should shorten with an ellipsis
            instead of shoving the action buttons on the right off-screen. */}
        {/* `min-w-44` is the wrap threshold, and it is doing real work. With
            the identity block free to shrink to nothing, flexbox preferred
            squeezing it over wrapping the rail beside it — at 720px in
            customize mode the title compressed to "O..", a truncation of a
            single word, while the rail beside it kept every pixel it asked
            for. A floor of 176px means the rail runs out of room first and
            wraps to its own line, which is the behaviour `flex-wrap` was there
            for. The `truncate` inside still works: it applies to the h2 and
            the description, which shrink within the floor rather than past
            it. */}
        <div className="min-w-44 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="min-w-0 truncate text-sm font-semibold text-foreground">
              {title}
            </h2>
            {/* The mode, stated where the eye already is. A solid primary chip
                against the title is the one place in this bar loud enough to
                be read without being looked for, which is what a mode that
                changes what a click does needs to be. */}
            {isEditing && (
              <span className="command-bar-mode-in inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-widget-label font-semibold text-primary-foreground">
                <SlidersHorizontal size={11} aria-hidden />
                {t("common:dashboard_grid.customizing")}
              </span>
            )}
          </div>
          {/* Its own line, so it is legible as the explanation of the region
              rather than as the fourth item in a row of chips. Still truncates
              — it is the least important thing here — but now it has the whole
              width of the identity block to truncate against instead of
              whatever two badges left it. */}
          <p className="mt-0.5 hidden truncate text-xs text-muted-foreground sm:block">
            {isEditing ? t("common:dashboard_grid.drag_hint") : description}
          </p>
        </div>

        {/* Status and actions travel together: on a screen too narrow for the
            whole rail it wraps to its own line as one unit, and past that
            scrolls within itself rather than the whole page gaining a
            horizontal scrollbar. `min-w-0` is what makes the scroll reachable
            — without it a flex item's default `min-width: auto` holds this to
            the full width of everything inside it. */}
        <div className="scrollbar-hidden flex min-w-0 shrink items-center gap-2 overflow-x-auto">
          {/* ── 2. Status ─────────────────────────────────────────────────
              Everything here is read. Nothing here is pressed, with one
              deliberate exception: a failed save turns into its own retry,
              because a failure you cannot act on is just bad news. */}
          <div className="flex shrink-0 items-center gap-2">
            <SaveIndicator
              state={saveState}
              isEditing={isEditing}
              onRetry={onRetrySave}
            />

            {/* Hidden on the narrowest screens rather than shrunk: provenance
                is the least urgent thing in the rail, and the first thing
                worth spending width on elsewhere. */}
            {/* `text-widget-label`, like everything else in this rail. It sat
                at `text-widget-caption` — 10px against the 11px of the save
                state on one side and the freshness reading on the other, three
                peers at two sizes, which reads as one of them being wrong
                rather than as any of them being ranked. The 10px step is for a
                badge nested inside a larger control (the count on Customize,
                the Esc key on Done); it is not for a peer in a row. */}
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-widget-label font-medium text-muted-foreground md:inline-flex">
              <span
                aria-hidden
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  SOURCE_DOT[source],
                )}
              />
              {sourceLabel[source]}
            </span>

            {/* The count, while arranging. In read mode it lives inside the
                Customize button instead — see there for why it is not a badge
                of its own any more. */}
            {isEditing && (
              <span className="command-bar-mode-in hidden shrink-0 text-widget-label tabular-nums text-muted-foreground lg:inline">
                {t("common:dashboard_grid.widget_count_of_available", {
                  count: widgetCount,
                  available: availableCount,
                })}
              </span>
            )}

            {/* Freshness as a reading, not as a button's label. There is
                nothing to say before the first refresh — the figures came from
                a cache of unknown age, and a freshness indicator that guesses
                is worse than none — so this simply is not rendered until the
                hook has something true to report. */}
            {!isEditing && freshnessLabel && (
              <span className="command-bar-mode-in hidden shrink-0 text-widget-label text-muted-foreground lg:inline">
                {freshnessLabel}
              </span>
            )}
          </div>

          {/* The line between what is read and what is pressed.
              `bg-current` at low opacity rather than `--color-border`: this bar
              has two grounds (plain and the customize tint) and a fixed neutral
              hairline disappeared into the second one. Inheriting the text
              colour keeps the same weight of separation on both. */}
          <span
            aria-hidden
            className="hidden h-6 w-px shrink-0 bg-current opacity-15 sm:block"
          />

          {/* ── 3. Actions ─────────────────────────────────────────────── */}
          <div className="flex shrink-0 items-center gap-1">
            {!isEditing && canRefresh && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Icon-only at every width now that the freshness reading
                      has its own place in the status rail. The label this used
                      to carry was that reading, which made one control both
                      the report and the act — and made it the widest thing in
                      the bar for a control most people press once a day. */}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    // Named explicitly: a tooltip's `aria-describedby` is a
                    // description, not a substitute for an accessible name.
                    aria-label={
                      isRefreshing
                        ? t("common:dashboard_grid.refreshing")
                        : freshnessLabel
                          ? t("common:dashboard_grid.refresh_with_freshness", {
                              freshnessLabel,
                            })
                          : t("common:buttons.refresh")
                    }
                    className="command-bar-mode-in text-muted-foreground"
                  >
                    <RefreshCw
                      size={14}
                      aria-hidden
                      className={cn(isRefreshing && "animate-spin")}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {freshnessLabel
                    ? t("common:dashboard_grid.refresh_every_widget_with", {
                        freshnessLabel: freshnessLabel.toLowerCase(),
                      })
                    : t("common:dashboard_grid.refresh_every_widget")}
                </TooltipContent>
              </Tooltip>
            )}

            {isEditing ? (
              <>
                {/* Plain controls in a row, with no box drawn round them.
                    They used to sit in a bordered, tinted segment, because on
                    an untinted bar three loose ghost buttons read as three
                    disconnected icons rather than one set of tools. The bar
                    itself now carries the mode — tinted ground, primary edge —
                    so the grouping is already done a level up, and keeping the
                    segment as well nested a bordered box inside a bordered bar
                    for no remaining reason. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onOpenLibrary}
                      aria-label={t("common:dashboard_grid.add_widget")}
                      className="command-bar-mode-in"
                    >
                      {/* Tone on the icon only: Add is the one constructive
                          action here, Reset is neutral. */}
                      <Plus size={14} aria-hidden className="text-primary" />
                      <span className="hidden sm:inline">
                        {t("common:buttons.add")}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {t("common:dashboard_grid.add_a_widget")}
                  </TooltipContent>
                </Tooltip>

                {/* Nothing to reset when this is already the default — the
                    button would delete a row that does not exist and report
                    success. `command-bar-item-in`: this mounts and unmounts on
                    its own (independent of the read/edit toggle, right after a
                    reset lands), so it gets its own entrance rather than the
                    shared mode-level fade. */}
                {isCustomized && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsResetOpen(true)}
                        aria-label={t(
                          "common:dashboard_grid.reset_to_default_layout",
                        )}
                        className="command-bar-item-in text-muted-foreground"
                      >
                        <RotateCcw size={14} aria-hidden />
                        <span className="hidden sm:inline">
                          {t("common:buttons.reset")}
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {t("common:dashboard_grid.reset_to_the_default_layout")}
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Set apart from the reversible actions beside it: everything
                    to the left can be undone by doing it again, this one ends
                    the mode and writes. */}
                <span
                  aria-hidden
                  className="mx-1 h-6 w-px shrink-0 bg-current opacity-15"
                />

                <Button
                  variant="primary"
                  size="sm"
                  onClick={onToggleEditing}
                  className="command-bar-mode-in"
                >
                  <Check size={14} aria-hidden />
                  {t("common:buttons.done")}
                  <kbd className="ms-0.5 hidden rounded-xs bg-primary-foreground/20 px-1 text-widget-caption leading-4 font-semibold md:inline">
                    Esc
                  </kbd>
                </Button>
              </>
            ) : (
              /* The count rides inside the button it has always triggered.
                 It used to be a separate pill that was styled as a badge and
                 behaved as a button — the one control in the bar whose
                 appearance said "reading" and whose behaviour said "press me"
                 — sitting on the opposite side of the bar from the Customize
                 button that did the same thing. One control, and the number
                 that motivates pressing it is on it. */
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleEditing}
                aria-label={t("common:dashboard_grid.customize_with_count", {
                  count: widgetCount,
                  available: availableCount,
                })}
                className="command-bar-mode-in"
              >
                <SlidersHorizontal size={14} aria-hidden />
                {t("common:dashboard_grid.customize")}
                <span
                  aria-hidden
                  className="ms-0.5 rounded-full bg-muted px-1.5 text-widget-caption font-semibold tabular-nums text-muted-foreground"
                >
                  {widgetCount}/{availableCount}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("common:dashboard_grid.reset_to_the_default_layout_confirm")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("common:dashboard_grid.arranged_widgets_will_be_replaced", {
                count: widgetCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common:dashboard_grid.keep_my_layout")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsResetOpen(false);
                onReset();
              }}
            >
              {t("common:dashboard_grid.reset_dashboard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Persistence is silent by default: the layout saves itself, so the only states
 * worth a pixel are "writing", a brief confirmation, and a failure the user needs
 * to know about.
 */
const SaveIndicator = ({
  state,
  isEditing,
  onRetry,
}: {
  state: TLayoutSaveState;
  isEditing: boolean;
  onRetry: () => void;
}) => {
  const { t } = useTranslation(["common"]);
  if (state === "idle") return null;
  if (state === "saved" && !isEditing) return null;

  const config = {
    pending: {
      icon: Loader2,
      label: t("common:dashboard_grid.saving"),
      className: "text-muted-foreground",
      spin: true,
    },
    saved: {
      icon: Check,
      label: t("common:dashboard_grid.saved"),
      className: "text-success",
      spin: false,
    },
    error: {
      icon: TriangleAlert,
      label: t("common:dashboard_grid.not_saved"),
      className: "text-danger",
      spin: false,
    },
  }[state];

  const Icon = config.icon;

  const body = (
    <>
      <Icon
        size={12}
        aria-hidden
        className={cn(config.spin && "animate-spin")}
      />
      {config.label}
    </>
  );

  // A failure the user cannot act on is just bad news. Retrying is the only
  // thing they would want here, so the indicator becomes the button rather than
  // sitting next to one.
  if (state === "error") {
    return (
      <button
        type="button"
        onClick={onRetry}
        aria-live="polite"
        className={cn(
          "widget-target widget-focus flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-widget-label font-semibold transition-colors",
          "hover:bg-danger-muted",
          config.className,
        )}
      >
        {body}
        <span className="underline underline-offset-2 opacity-80">
          {t("common:buttons.retry")}
        </span>
      </button>
    );
  }

  return (
    <span
      aria-live="polite"
      className={cn(
        "flex shrink-0 items-center gap-1 px-1 text-widget-label font-semibold",
        config.className,
      )}
    >
      {body}
    </span>
  );
};
